/**
 * What a vendor sees on a listing they took from the catalogue.
 *
 * The photos were the reported gap — a vendor picked a product, saw an empty upload box,
 * and reasonably concluded their listing had none. It had three; they just belonged to the
 * master and nothing on this screen said so.
 *
 * So this shows the whole inherited half in one block: the gallery, and every field the
 * platform maintains. It doubles as the explanation for why those fields are not editable
 * here — "these come from Pet Market" is only reassuring if you can see what they are.
 */
import { Boxes, Lock } from 'lucide-react';
import { AuthImage } from '@/components/auth-image';
import { cn } from '@/lib/utils';

export type CatalogSummary = {
  name: string;
  brand: string | null;
  category: string;
  pack_size: string | null;
  description: string | null;
  is_prescription_required?: boolean;
  /** Every curated photo, in order. */
  catalog_images?: { id: string; path: string }[];
  /** The primary photo, for listings whose gallery has not loaded yet. */
  catalog_image_path?: string | null;
};

const pretty = (s: string) =>
  s
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');

export function CatalogProductSummary({ product, className }: { product: CatalogSummary; className?: string }) {
  const images =
    product.catalog_images && product.catalog_images.length > 0
      ? product.catalog_images
      : product.catalog_image_path
        ? [{ id: 'primary', path: product.catalog_image_path }]
        : [];

  const facts = [
    ['Brand', product.brand],
    ['Category', pretty(product.category)],
    ['Pack size', product.pack_size],
  ].filter(([, v]) => !!v) as [string, string][];

  return (
    <div
      className={cn('rounded-card border-2 border-brand-outline-variant/15 bg-secondary/25 p-3', className)}
      data-testid="catalog-summary"
    >
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Lock className="h-3.5 w-3.5" />
        From the Pet Market catalogue — kept up to date for you
      </p>

      {images.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="catalog-summary-no-images">
          No catalogue photos yet. Add your own below and shoppers will see those.
        </p>
      ) : (
        <ul className="mb-2 flex gap-2 overflow-x-auto pb-1" data-testid="catalog-summary-images">
          {images.map((img, i) => (
            <li key={img.id}>
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-input border-2 border-brand-outline-variant/20">
                <AuthImage
                  path={`${img.path}?variant=thumb`}
                  alt={`${product.name} photo ${i + 1}`}
                  className="h-full w-full object-cover"
                />
                {i === 0 && (
                  /* Naming the primary matters: it is the one on every shop card. */
                  <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[10px] font-semibold text-white">
                    Main
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {facts.map(([label, value]) => (
          <div key={label} className="flex gap-1">
            <dt className="text-muted-foreground">{label}:</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
        {product.is_prescription_required && (
          <div className="flex gap-1">
            <dt className="text-muted-foreground">Prescription:</dt>
            <dd className="font-semibold text-destructive">required</dd>
          </div>
        )}
      </dl>

      {product.description && (
        <p className="mt-1.5 line-clamp-3 text-xs text-muted-foreground" data-testid="catalog-summary-description">
          {product.description}
        </p>
      )}

      {images.length > 0 && (
        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Boxes className="h-3 w-3" />
          Shoppers see these first, then any photo you add below.
        </p>
      )}
    </div>
  );
}
